import { useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form'
import { View } from 'react-native'
import { MultiSelectProps } from 'react-native-element-dropdown/lib/typescript/components/MultiSelect/model'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import { getErrorField } from '@/components/form/formUtils'
import Skeleton from '@/components/shared/Skeleton'
import UniChip from '@/components/unistyles/UniChip'
import UniHelperText from '@/components/unistyles/UniHelperText'
import UniMultiSelect from '@/components/unistyles/UniMultiSelect'
import UniText from '@/components/unistyles/UniText'

type data = { label: string; value: string }

interface Props<C extends FieldValues, E extends FieldErrors> {
  control: Control<C, any>
  data: data[]
  errors?: E
  fieldName: Path<C>
  isLoading?: boolean
  label: string
  required: boolean
}

const FormFieldMultiSelect = <C extends FieldValues, E extends FieldErrors>({
  control,
  data,
  errors,
  fieldName,
  label,
  required,
  style,
  isLoading,
  ...props
}: Props<C, E> & Partial<MultiSelectProps<data>>) => {
  const { theme } = useUnistyles()

  const [selectedDropdownValues, setSelectedDropdownValues] = useState<
    string[] | null
  >(null)

  const renderLabel = () => {
    if (selectedDropdownValues?.length || control._formValues[fieldName]) {
      return <UniText style={styles.label}>{label}</UniText>
    }
    return null
  }

  return (
    <View style={[style, styles.root]}>
      <Skeleton isLoading={isLoading}>
        <Controller
          control={control}
          name={fieldName}
          render={({ field: { onChange, onBlur, value } }) => (
            <View style={[style, { marginTop: 6 }]}>
              {renderLabel()}
              <UniMultiSelect
                data={data}
                dropdownPosition="auto"
                getThemeProps={(t) => ({
                  activeColor: t.colors.primaryContainer,
                  containerStyle: {
                    backgroundColor: t.colors.background,
                    borderColor: t.colors.outline,
                    maxHeight: '80%' as const,
                  },
                  iconColor: getErrorField(fieldName, errors)
                    ? t.colors.error
                    : t.colors.onBackground,
                  itemContainerStyle: {
                    backgroundColor: t.colors.background,
                  },
                  itemTextStyle: { color: t.colors.onBackground },
                  placeholderStyle: [
                    { color: t.colors.onSurface },
                    getErrorField(fieldName, errors) && {
                      color: t.colors.error,
                    },
                  ],
                  selectedTextStyle: { color: t.colors.onPrimary },
                })}
                inverted={false}
                itemTestIDField={`${fieldName}-multiSelect-item`}
                labelField="label"
                onBlur={onBlur}
                onChange={(selected) => {
                  setSelectedDropdownValues(selected)
                  onChange(selected)
                }}
                placeholder={label}
                renderSelectedItem={(item, unselect) => {
                  return (
                    <UniChip
                      icon="close"
                      onPress={() => {
                        if (unselect) {
                          unselect(item)
                        }
                      }}
                      style={styles.chipStyle}
                    >
                      {item.label}
                    </UniChip>
                  )
                }}
                selectedStyle={styles.chipStyle}
                selectedTextProps={{ testID: `${fieldName}-multiSelect-text` }}
                style={[
                  styles.dropdown,
                  getErrorField(fieldName, errors) && {
                    borderColor: theme.colors.error,
                    borderWidth: 2,
                  },
                ]}
                testID={`${fieldName}-multiSelect`}
                value={value}
                valueField="value"
                {...props}
              />
              {getErrorField(fieldName, errors) && (
                <UniHelperText
                  type="error"
                  visible={!!getErrorField(fieldName, errors)}
                >
                  {getErrorField(fieldName, errors)?.message as string}
                </UniHelperText>
              )}
            </View>
          )}
          rules={{
            required: required,
          }}
        />
      </Skeleton>
    </View>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    chipStyle: {
      margin: theme.tokens.spacing[2],
    },
    dropdown: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.outline,
      borderRadius: 4,
      borderWidth: 1,
      height: 50,
      paddingHorizontal: 12,
    },
    // The values in this style are intentionally hardcoded to match the design of react-native-paper
    // this allows the component to be used as a near-visual-drop-in replacement for react-native-paper's text input
    label: {
      backgroundColor: theme.colors.surface,
      color: theme.colors.onSurfaceVariant,
      fontSize: 12,
      left: 5,
      paddingHorizontal: theme.tokens.spacing[2],
      position: 'absolute',
      top: -8,
      zIndex: 999,
    },
    root: {
      marginBottom: theme.tokens.spacing[2],
    },
  }
})

export default FormFieldMultiSelect
